import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button as Button } from '@factorialco/f0-react'
import { F0Select } from '@factorialco/f0-react'
import { EntityId, EntitySelect, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import { WorkflowRoleSelectorDocument } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import JobCatalogRoleSelector from './JobCatalogRoleSelector'
import LegacyRoleSelector from './LegacyRoleSelector'
import { type StrategyItem, getStrategyOptions, getStrategyType } from './manualApproveHandlerUtils'

interface ApprovalSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  onAddStrategies: (strategies: StrategyItem[]) => void
  availableEmployees: Array<{
    id: number
    fullName: string
    avatar?: { url?: string | null } | null
  }>
  availableTeams: Array<{ id: number; name: string }>
  employeesLoading: boolean
  teamsLoading: boolean
  existingStrategies: StrategyItem[]
}

export const ApprovalSettingsModal = ({
  isOpen,
  onClose,
  onAddStrategies,
  availableEmployees,
  availableTeams,
  employeesLoading,
  teamsLoading,
  existingStrategies,
}: ApprovalSettingsModalProps) => {
  const [strategy, setStrategy] = useState<string>('')
  const [selectedEmployeesIds, setSelectedEmployeesIds] = useState<EntityId[]>([])
  const [selectedTeamsIds, setSelectedTeamsIds] = useState<EntityId[]>([])
  const [selectedRoleId, setSelectedRoleId] = useState<string | undefined>(undefined)

  const { company } = useCurrentSession()

  const { data: rolesData } = useQuery(WorkflowRoleSelectorDocument, {
    variables: { companyId: company.id },
  })

  const isLegacyJobCatalog =
    rolesData?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === 'legacy_job_catalog'
    )?.enabled ?? true

  const availableRoles = rolesData?.jobCatalog?.rolesConnection?.nodes || []
  const treeRoles = rolesData?.jobCatalog?.treeNodesConnection?.nodes || []

  if (!isOpen) return null

  const resetModal = () => {
    setStrategy('')
    setSelectedEmployeesIds([])
    setSelectedTeamsIds([])
    setSelectedRoleId(undefined)
  }

  const handleClose = () => {
    resetModal()
    onClose()
  }

  const handleSave = () => {
    if (!strategy) {
      return
    }

    const strategyType = getStrategyType(strategy)
    const strategiesToAdd: StrategyItem[] = []

    if (strategyType === 'manual_selected_employees' && selectedEmployeesIds.length > 0) {
      selectedEmployeesIds.forEach((employeeId) => {
        if (typeof employeeId === 'number') {
          strategiesToAdd.push({
            id: `manual_selection_${employeeId}`,
            type: 'manual_selection',
            employeeId: employeeId,
          })
        }
      })
    } else if (strategyType === 'team' && selectedTeamsIds.length > 0) {
      const teamId = selectedTeamsIds[0]
      if (typeof teamId === 'number') {
        strategiesToAdd.push({
          id: `${strategy}_${teamId}_${Date.now()}`,
          type: strategy,
          teamId: teamId,
        })
      }
    } else if (strategyType === 'role' && selectedRoleId) {
      strategiesToAdd.push({
        id: `${strategy}_${selectedRoleId}_${Date.now()}`,
        type: strategy,
        roleId: selectedRoleId,
      })
    } else if (strategyType === 'none') {
      strategiesToAdd.push({
        id: `${strategy}_${Date.now()}`,
        type: strategy,
      })
    }

    if (strategiesToAdd.length > 0) {
      onAddStrategies(strategiesToAdd)
    }

    resetModal()
    onClose()
  }

  const selectedEmployeeIds = existingStrategies
    .filter((s) => s.type === 'manual_selection' && s.employeeId !== undefined)
    .map((s) => s.employeeId)
    .filter((id): id is number => id !== undefined)

  const filteredEmployees = availableEmployees.filter(
    (emp) => !selectedEmployeeIds.includes(emp.id)
  )

  const getFilteredTeamsForStrategy = (strategyType: string) => {
    const usedTeamIdsForThisStrategy = existingStrategies
      .filter((s) => s.type === strategyType && s.teamId !== undefined)
      .map((s) => s.teamId)
      .filter((id): id is number => id !== undefined)

    return availableTeams.filter((team) => !usedTeamIdsForThisStrategy.includes(team.id))
  }

  const selectedEmployeesEntities = availableEmployees
    .filter((emp) => selectedEmployeesIds.includes(emp.id))
    .map((emp) => ({
      id: emp.id,
      name: emp.fullName,
      avatar: buildUrl(emp.avatar?.url),
    }))

  const selectedTeamsEntities = availableTeams
    .filter((team) => selectedTeamsIds.includes(team.id))
    .map((team) => ({
      id: team.id,
      name: team.name,
    }))

  const strategyType = getStrategyType(strategy)

  const isSaveDisabled = (() => {
    if (!strategy) return true

    if (strategyType === 'none') return false

    if (strategyType === 'manual_selected_employees') {
      return selectedEmployeesIds.length === 0
    }

    if (strategyType === 'team') {
      return selectedTeamsIds.length === 0
    }

    if (strategyType === 'role') {
      return !selectedRoleId
    }

    return false
  })()

  const filteredTeams = strategyType === 'team' ? getFilteredTeamsForStrategy(strategy) : []

  const usedNoneTypeStrategies = existingStrategies
    .filter((s) => getStrategyType(s.type) === 'none')
    .map((s) => s.type)

  const filteredStrategyOptions = getStrategyOptions().filter((option) => {
    if (option.ui_type === 'none') {
      return !usedNoneTypeStrategies.includes(option.value)
    }
    return true
  })

  return (
    <Modal
      title={i18n.t('workflows.sidepanel.approval.add_approvers.title')}
      footer={
        <>
          <Button
            label={i18n.t('common.cancel')}
            variant="outline"
            size="md"
            onClick={handleClose}
          />
          <Button
            label={i18n.t('common.save_changes')}
            variant="default"
            size="md"
            onClick={handleSave}
            disabled={isSaveDisabled}
          />
        </>
      }
      onClose={handleClose}
    >
      <Box gap="s16">
        <Box gap="s8" width="full">
          <Text size="200" weight="medium" className="text-f1-foreground-secondary">
            {i18n.t('workflows.sidepanel.approval.fields.add_title')}
          </Text>

          <F0Select
            size="md"
            label="Main approval user"
            hideLabel
            value={strategy}
            onChange={(value: string) => setStrategy(value)}
            options={filteredStrategyOptions.map(({ ui_type: _, ...option }) => option)}
          />

          {getStrategyType(strategy) === 'manual_selected_employees' && (
            <EntitySelect
              label={i18n.t('workflows.sidepanel.approval.strategies.selectors.employees')}
              hideLabel
              size="md"
              loading={employeesLoading}
              entities={filteredEmployees.map((emp) => ({
                id: emp.id,
                name: emp.fullName,
                avatar: buildUrl(emp.avatar?.url),
                expanded: false,
              }))}
              notFoundSubtitle={i18n.t(
                'workflows.sidepanel.approval.strategies.selectors.employees_not_found_subtitle'
              )}
              notFoundTitle={i18n.t(
                'workflows.sidepanel.approval.strategies.selectors.employees_not_found_title'
              )}
              onGroupChange={() => {}}
              onItemExpandedChange={() => {}}
              onSelect={(selection: EntitySelectEntity[] | EntitySelectEntity | null) => {
                if (selection === null) {
                  setSelectedEmployeesIds([])
                  return
                }

                setSelectedEmployeesIds(
                  Array.isArray(selection) ? selection.map((s) => s.id) : [selection.id]
                )
              }}
              searchPlaceholder={i18n.t(
                'workflows.sidepanel.approval.strategies.selectors.employees_search_placeholder'
              )}
              selectedEntities={selectedEmployeesEntities}
              selectedLabel={i18n.t(
                'workflows.sidepanel.approval.strategies.selectors.employees_selected_label'
              )}
              singleSelector={false}
              placeholder={i18n.t(
                'workflows.sidepanel.approval.strategies.selectors.employees_placeholder'
              )}
              selectedItemsCopy={i18n.t(
                'workflows.sidepanel.approval.strategies.selectors.employees_selected_copy'
              )}
              groups={[]}
              selectedGroup="all"
              clearLabel={i18n.t(
                'workflows.sidepanel.approval.strategies.selectors.employees_clear_label'
              )}
              selectAllLabel={i18n.t(
                'workflows.sidepanel.approval.strategies.selectors.employees_select_all_label'
              )}
            />
          )}

          {strategyType === 'team' && (
            <EntitySelect
              label={i18n.t('workflows.sidepanel.approval.strategies.selectors.teams')}
              hideLabel
              size="md"
              loading={teamsLoading}
              entities={filteredTeams.map((team) => ({
                id: team.id,
                name: team.name,
                expanded: false,
              }))}
              notFoundSubtitle={i18n.t(
                'workflows.sidepanel.approval.strategies.selectors.teams_not_found_subtitle'
              )}
              notFoundTitle={i18n.t(
                'workflows.sidepanel.approval.strategies.selectors.teams_not_found_title'
              )}
              onGroupChange={() => {}}
              onItemExpandedChange={() => {}}
              onSelect={(selection: EntitySelectEntity[] | EntitySelectEntity | null) => {
                if (selection === null) {
                  setSelectedTeamsIds([])
                  return
                }

                const teamId = Array.isArray(selection) ? selection[0]?.id : selection.id
                setSelectedTeamsIds(teamId ? [teamId] : [])
              }}
              searchPlaceholder={i18n.t(
                'workflows.sidepanel.approval.strategies.selectors.teams_search_placeholder'
              )}
              selectedEntities={selectedTeamsEntities}
              selectedLabel={i18n.t(
                'workflows.sidepanel.approval.strategies.selectors.teams_selected_label'
              )}
              singleSelector
              placeholder={i18n.t(
                'workflows.sidepanel.approval.strategies.selectors.teams_placeholder'
              )}
              selectedItemsCopy={i18n.t(
                'workflows.sidepanel.approval.strategies.selectors.teams_selected_copy'
              )}
              groups={[]}
              selectedGroup="all"
              clearLabel={i18n.t(
                'workflows.sidepanel.approval.strategies.selectors.teams_clear_label'
              )}
              selectAllLabel={i18n.t(
                'workflows.sidepanel.approval.strategies.selectors.teams_select_all_label'
              )}
            />
          )}

          {strategyType === 'role' && isLegacyJobCatalog && (
            <LegacyRoleSelector
              availableRoles={availableRoles}
              value={selectedRoleId}
              onChange={(id) => setSelectedRoleId(id ?? undefined)}
            />
          )}

          {strategyType === 'role' && !isLegacyJobCatalog && (
            <JobCatalogRoleSelector
              treeRoles={treeRoles}
              value={selectedRoleId}
              onChange={(uuid) => {
                setSelectedRoleId(uuid ?? undefined)
              }}
            />
          )}
        </Box>
      </Box>
    </Modal>
  )
}
